import React from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { hasCanUsePlatesPermission } from '@paragon/login-tools';

import { setLever, setMaxPlies } from '../../../../store/slices/optimizationSettingsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';

// Keep in sync with MaxOptimizationPlies in Optimization.cs.
const MAX_OPTIMIZATION_PLIES = 6;
const PLY_COUNT_OPTIONS = Array.from({ length: MAX_OPTIMIZATION_PLIES }, (_, index) => index + 1);

interface Props {
  readonly disabled: boolean;
}

export function OptimizerLeversSection({ disabled }: Props) {
  const dispatch = useAppDispatch();

  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const optimizationLevers = useAppSelector((state) => state.optimizationSettings.levers);
  const maxPlies = useAppSelector((state) => state.optimizationSettings.maxPlies);

  const maxPliesDisabled = disabled || !optimizationLevers.varyPlyCount;

  return (
    <Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={optimizationLevers.resizeChords}
            disabled={disabled}
            onChange={(_event, checked) => dispatch(setLever({ lever: 'resizeChords', value: checked }))}
          />
        }
        label="Resize chords"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={canUsePlates && optimizationLevers.plating}
            disabled={!canUsePlates || disabled}
            onChange={(_event, checked) => dispatch(setLever({ lever: 'plating', value: checked }))}
          />
        }
        label="Optimize plating"
      />

      <FormControlLabel
        // Indented subfield
        sx={{ ml: 3 }}
        control={
          <Checkbox
            checked={optimizationLevers.exhaustivePlating}
            disabled={disabled || !canUsePlates || !optimizationLevers.plating}
            onChange={(_event, checked) => dispatch(setLever({ lever: 'exhaustivePlating', value: checked }))}
          />
        }
        label="Try off-center/rotated plating"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={optimizationLevers.varySplicing}
            disabled={disabled}
            onChange={(_event, checked) => dispatch(setLever({ lever: 'varySplicing', value: checked }))}
          />
        }
        label="Optimize splices"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <FormControlLabel
          sx={{ flex: 1, mr: 0 }}
          control={
            <Checkbox
              checked={optimizationLevers.varyPlyCount}
              disabled={disabled}
              onChange={(_event, checked) => dispatch(setLever({ lever: 'varyPlyCount', value: checked }))}
            />
          }
          label="Optimize ply count"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color={maxPliesDisabled ? 'text.disabled' : 'text.secondary'}>
            up to
          </Typography>
          <TextField
            select
            size="small"
            value={maxPlies}
            onChange={(event) => dispatch(setMaxPlies(Number(event.target.value)))}
            disabled={maxPliesDisabled}
            sx={{ width: 72 }}
            slotProps={{ htmlInput: { 'aria-label': 'Maximum ply count to try' } }}
          >
            {PLY_COUNT_OPTIONS.map((plies) => (
              <MenuItem key={plies} value={plies}>
                {plies}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Stack>
  );
}
